import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Logo from "@assets/icons/Logo";
import ArrowRight from "@assets/icons/ArrowRight";
import styles from "./NavigationGuide.module.scss";

const margin = 16;

const NavigationGuide = ({ items, step, onNext, onClose }) => {
  const { t } = useTranslation();
  const id = useId();
  const dialogRef = useRef(null);
  const nextRef = useRef(null);
  const skipRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [cardHeight, setCardHeight] = useState(200);
  const currentIndex = items?.findIndex((item) => item.name === step) ?? -1;
  const currentItem = items?.[currentIndex];

  useEffect(() => {
    if (step && Array.isArray(items) && !currentItem) onNext();
  }, [step, items, currentItem, onNext]);

  useLayoutEffect(() => {
    if (!currentItem) {
      setPosition(null);
      return undefined;
    }
    let target;
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => update()) : null;
    const update = () => {
      const element = document.querySelector(`[data-navigation-guide="${currentItem.name}"]`);
      if (!element) {
        setPosition(null);
        return;
      }
      if (element !== target) {
        if (target) resizeObserver?.unobserve(target);
        target = element;
        resizeObserver?.observe(target);
        target.scrollIntoView?.({ block: "nearest", inline: "nearest" });
      }
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const next = {
        left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom,
        width: rect.width, height: rect.height,
        viewportWidth: window.innerWidth, viewportHeight: window.innerHeight,
      };
      setPosition((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", update);
    document.addEventListener("scroll", update, true);
    return () => {
      observer.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", update);
      document.removeEventListener("scroll", update, true);
    };
  }, [currentItem]);

  const isVisible = !!currentItem && !!position;
  useLayoutEffect(() => {
    if (!isVisible) return undefined;
    const previousFocus = document.activeElement;
    return () => {
      if (previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, [isVisible]);

  useLayoutEffect(() => {
    if (!isVisible) return undefined;
    dialogRef.current?.focus({ preventScroll: true });
    const measure = () => setCardHeight(dialogRef.current?.getBoundingClientRect().height || 200);
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (dialogRef.current) observer?.observe(dialogRef.current);
    return () => observer?.disconnect();
  }, [isVisible, step]);

  if (!isVisible) return null;

  const rightWidth = position.viewportWidth - position.right - margin * 2;
  const leftWidth = position.left - margin * 2;
  const side = rightWidth >= 180 ? "right" : leftWidth >= 180 ? "left" : null;
  const width = Math.min(340, side ? side === "right" ? rightWidth : leftWidth : position.viewportWidth - margin * 2);
  const left = side === "right" ? position.right + margin : side === "left" ? position.left - margin - width : margin;
  const below = position.viewportHeight - position.bottom - margin * 2;
  const verticalTop = below >= cardHeight ? position.bottom + margin : position.top - cardHeight - margin;
  const top = Math.max(margin, Math.min(
    side ? position.top : verticalTop,
    position.viewportHeight - cardHeight - margin,
  ));
  const pointerTop = Math.max(24, Math.min(position.top + position.height / 2 - top, cardHeight - 24));

  return createPortal(
    <div className={styles.layer}>
      <button type="button" aria-hidden="true" tabIndex={-1} className={styles.backdrop} onClick={onNext} />
      <div aria-hidden="true" className={styles.spotlight} style={{
        left: Math.max(0, position.left - 4), top: Math.max(0, position.top - 4),
        width: position.width + 8, height: position.height + 8,
      }} />
      {side && <span aria-hidden="true" className={styles.pointer} style={{ left: side === "right" ? left - 7 : left + width - 7, top: top + pointerTop }} />}
      <section
        ref={dialogRef} role="dialog" aria-modal="true" tabIndex={-1}
        aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
        className={styles.callout}
        style={{ left, top, width, maxHeight: position.viewportHeight - margin * 2 }}
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
          if (event.key === "Tab") {
            event.preventDefault();
            const buttons = [nextRef.current, skipRef.current];
            const index = buttons.indexOf(document.activeElement);
            const nextIndex = index === -1
              ? event.shiftKey ? buttons.length - 1 : 0
              : (index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
            buttons[nextIndex]?.focus();
          }
        }}
      >
        <div className={styles.header}>
          <div className={styles.brand}>
            <Logo size="" className={styles.brandIcon} aria-hidden="true" focusable="false" />
            <span>{t("navigationGuide.title")}</span>
          </div>
          <p className={styles.step} aria-live="polite">{t("navigationGuide.step", { current: currentIndex + 1, total: items.length })}</p>
        </div>
        <div className={styles.progress} aria-hidden="true">
          {items.map((item, index) => <span key={item.name} data-state={index < currentIndex ? "complete" : index === currentIndex ? "current" : "upcoming"} />)}
        </div>
        <h2 id={`${id}-title`} className="font-bold-poppins">{t(currentItem.titleKey)}</h2>
        <p id={`${id}-description`} className={styles.description}>{t(`navigationGuide.descriptions.${currentItem.descriptionKey}`, {
          defaultValue: t("navigationGuide.defaultDescription", { title: t(currentItem.titleKey) }),
        })}</p>
        <div className={styles.actions}>
          <button ref={nextRef} type="button" className={styles.next} onClick={onNext}>
            {t(currentIndex === items.length - 1 ? "common.done" : "common.next")}
            {currentIndex < items.length - 1 && <ArrowRight color="currentColor" aria-hidden="true" focusable="false" />}
          </button>
          <button ref={skipRef} type="button" className={styles.skip} onClick={onClose}>{t("common.skip")}</button>
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default NavigationGuide;
